import { DropdownOptionsService } from './../../../../core/services/dropdown-options.service';
import { ContainerService } from '../../services/container-service.service';
import { Component, OnInit, OnChanges, SimpleChanges, forwardRef, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';

export const CARTYPE_SELECTOR_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CarTypeSelectorComponent),
  multi: true
};

@Component({
  selector: 'laas-car-type-selector',
  templateUrl: './car-type-selector.component.html',
  styleUrls: ['./car-type-selector.component.scss'],
  providers: [CARTYPE_SELECTOR_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CarTypeSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() disabled: boolean;
  @Input() panelClass: any;
  @Input() panelStyle: any;
  /** 若傳入 facility，改從 ContainerCarType 取得該倉車型清單；否則沿用 FacilityOptions (UOM) */
  @Input() facility: string | null | undefined = null;
  @Output() valueChange: EventEmitter<any>;
  isOpenModal: boolean;
  onModelChange: (obj: any) => void;
  onModelTouched: (obj: any) => void;
  displayCarTypeStrs: string[];
  value: any;
  carTypeOptions: any[];
  form: FormGroup;
  submitted: boolean;

  constructor(
    private dropdownOptionService: DropdownOptionsService,
    private containerService: ContainerService,
    private logger: NGXLogger,
    private fb: FormBuilder) {
    this.disabled = false;
    this.valueChange = new EventEmitter<any>();
    this.isOpenModal = false;
    this.onModelChange = this.onModelTouched = (obj: any) => { };
    this.carTypeOptions = [];
    this.displayCarTypeStrs = [];
    this.form = this.fb.group({
      carTypes: this.fb.array([])
    });
    this.submitted = false;
  }

  ngOnInit(): void {
    this.loadCarTypeOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.facility && !changes.facility.firstChange) {
      this.loadCarTypeOptions();
    }
  }

  private loadCarTypeOptions(): void {
    const options$: Observable<any[]> = this.facility
      ? this.containerService.getCarType(this.facility).pipe(
          map((items: any[]) => items.map(x => ({ optionCode: x.carType, optionDesc: `${x.carType} - ${x.carCnDesc}` })))
        )
      : this.dropdownOptionService.getCarTypeOptions();

    options$.subscribe({
      next: (options: any[]) => {
        this.carTypeOptions = options.map(x => ({ label: x.optionDesc, value: x.optionCode, count: null, isSelected: false }));
        const carTypes = this.form.controls.carTypes as FormArray;
        carTypes.clear();
        this.carTypeOptions.forEach(x => {
          const group = this.fb.group({
            count: [null, Validators.required]
          });
          carTypes.push(group);
        });
        this.writeValue(this.value);
      }
    });
  }

  writeValue(obj: any): void {
    this.value = obj;
    const carTypesObj = isNullOrEmpty(this.value) ? null : this.convertToCarTypeObject(this.value);
    this.mapCarTypeOptions(carTypesObj);
    const selectedCarTypes = this.carTypeOptions.filter(x => x.isSelected);
    this.displayCarTypeStrs = selectedCarTypes.map(x => this.toCarTypeInfoDisplayText(x));
  }

  registerOnChange(fn: (obj: any) => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onModelTouched = fn;
  }

  toogleBtnClick(event: any): void {
    this.isOpenModal = true;
  }

  confirmBtnClick(event: any): void {
    let isValid = true;
    for (let i = 0; i < this.carTypeOptions.length; i++) {
      const option = this.carTypeOptions[i];
      if (!option.isSelected) {
        continue;
      }
      if (this.getFormControl(i, 'count')?.invalid) {
        isValid = isValid && false;
      }
    }
    if (!isValid) {
      this.submitted = true;
      return;
    }
    const selectedCarTypes = this.carTypeOptions.filter(x => x.isSelected);
    this.displayCarTypeStrs = selectedCarTypes.map(x => this.toCarTypeInfoDisplayText(x));
    this.value = this.toValueString(selectedCarTypes);
    this.onModelChange(this.value);
    this.valueChange.emit({ originalEvent: event, data: this.value });
    this.isOpenModal = false;
    this.submitted = false;
    this.logger.info(this.value);
  }

  cancelBtnClick(event: any): void {
    this.isOpenModal = false;
  }

  checkBoxChange(event: any, index: number): void {
    if (!event.checked) {
      this.getFormControl(index, 'count')?.reset();
    }
  }

  isShowValidationMsg(control: AbstractControl | null): boolean {
    if (isNullOrEmpty(control)) {
      return false;
    }
    const tmpCtrl = control as any;
    return tmpCtrl.invalid && (this.submitted || tmpCtrl.dirty);
  }

  getFormControl(index: number, ctrlName: string): AbstractControl | null {
    const fgArray = this.form.controls.carTypes as FormArray;
    return fgArray.controls[index].get(ctrlName);
  }

  private convertToCarTypeObject(rawStr: string): any {
    return JSON.parse(`{${rawStr}}`.replace(/(\{|,)\s*(.+?)\s*:/g, '$1 "$2":'));
  }

  private mapCarTypeOptions(carTypesObj: any): void {
    carTypesObj = carTypesObj ?? {};
    for (const option of this.carTypeOptions) {
      if (Object.keys(carTypesObj).indexOf(option.value) > -1) {
        option.count = carTypesObj[option.value];
        option.isSelected = true;
      } else {
        option.count = null;
        option.isSelected = false;
      }
    }
  }

  private toValueString(carTypes: any[]): string {
    const str = carTypes.map(x => `${x.value}:${x.count}`).join(',');
    return str;
  }

  private toCarTypeInfoDisplayText(carType: any): string {
    return `${carType.label} x ${carType.count}`;
  }
}
